// src/features/settings/SettingsPage.tsx
import { useState, useEffect, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGuild } from '../../lib/GuildContext';
import { formatGuildName } from '../../lib/formatGuildName';
import { useUpdateGeneralSettings, type GeneralSettings } from './useGeneralSettings';
import { RolePicker } from '../../components/pickers/RolePicker';
import { apiFetch } from '../../lib/api';
import './SettingsPage.css';

const SNOWFLAKE_RE = /^\d{17,20}$/;

function useBroadcastOptOut(guildId: string | null) {
  return useQuery({
    queryKey: ['broadcast-opt-out', guildId],
    queryFn: () => apiFetch<{ optedOut: boolean }>(`/api/guilds/${guildId}/broadcast-opt-out`),
    enabled: !!guildId,
  });
}

function useSetBroadcastOptOut(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (optedOut: boolean) =>
      apiFetch(`/api/guilds/${guildId}/broadcast-opt-out`, { method: 'PATCH', body: JSON.stringify({ optedOut }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['broadcast-opt-out', guildId] }),
  });
}

function BroadcastOptOutCard({ guildId }: { guildId: string | null }) {
  const { data, isLoading } = useBroadcastOptOut(guildId);
  const setOptOut = useSetBroadcastOptOut(guildId);

  return (
    <article className="card" style={{ marginTop: '1.25rem' }}>
      <header className="card__header"><h2 className="card__title">Announcements</h2></header>
      <div className="card__content">
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={!isLoading && !!data?.optedOut}
            disabled={isLoading || setOptOut.isPending}
            onChange={(e) => setOptOut.mutate(e.target.checked)}
          />
          Opt out of occasional announcement messages from the bot owner, for this server
        </label>
        <p style={{ color: 'var(--text-muted)', fontSize: 11, margin: '0.3rem 0 0' }}>
          These are rare, direct messages about things like new features or planned downtime — not
          related to your servers&apos; own settings. This applies to this server specifically — if
          you own another server and want it to keep receiving them, that one is unaffected either
          way.
        </p>
      </div>
    </article>
  );
}

export function SettingsPage() {
  const { guilds, selectedGuildId } = useGuild();
  const guild = guilds.find((g) => g.guild_id === selectedGuildId);
  const update = useUpdateGeneralSettings(selectedGuildId);

  const [modRoleId, setModRoleId] = useState('');
  const [vipRoleId, setVipRoleId] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!guild) return;
    const s = guild.settings as GeneralSettings;
    setModRoleId(s.modRoleId ?? '');
    setVipRoleId(s.vipRoleId ?? '');
  }, [guild]);

  const modRoleValid = modRoleId === '' || SNOWFLAKE_RE.test(modRoleId);
  const vipRoleValid = vipRoleId === '' || SNOWFLAKE_RE.test(vipRoleId);
  const isValid = modRoleValid && vipRoleValid;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isValid) return;

    // Sends modRoleId/vipRoleId: null explicitly when cleared, rather than
    // omitting the key — omitting it just leaves the old value
    // untouched (the backend does a JSONB merge, not a replace), so
    // there was previously no way to actually clear this once set.
    const settings: GeneralSettings = { modRoleId: modRoleId || null, vipRoleId: vipRoleId || null };

    update.mutate(settings, {
      onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2500); },
    });
  }

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">{guild ? formatGuildName(guild.guild_name, guild.guild_id) : 'No server selected'}</p>
        <h1 className="page-header__title">Settings</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <article className="card">
          <header className="card__header"><h2 className="card__title">General</h2></header>
          <div className="card__content settings-stack">
            <div className="settings-field">
              <label htmlFor="modRoleId">Moderator role ID (optional)</label>
              <RolePicker
                guildId={selectedGuildId}
                value={modRoleId}
                onChange={setModRoleId}
                placeholder="e.g. 123456789012345678"
              />
              <p style={{ color: 'var(--text-muted)', fontSize: 11, margin: '0.3rem 0 0' }}>
                Members with this Discord role get moderator-level bot permissions, separate from
                individually-granted access on the Permissions page.
              </p>
              {!modRoleValid && <p className="settings-field__error">Must be a Discord snowflake — 17 to 20 digits.</p>}
            </div>

            <div className="settings-field">
              <label htmlFor="vipRoleId">VIP role ID (optional)</label>
              <RolePicker
                guildId={selectedGuildId}
                value={vipRoleId}
                onChange={setVipRoleId}
                placeholder="e.g. 123456789012345678"
              />
              <p style={{ color: 'var(--text-muted)', fontSize: 11, margin: '0.3rem 0 0' }}>
                Members with this Discord role get VIP status for bot features that check for it
                (e.g. an optional VIP-only gate on <code>/profit</code>, currently off by default).
                Most servers already have an existing role for this — boosters, a paid tier, etc.
              </p>
              {!vipRoleValid && <p className="settings-field__error">Must be a Discord snowflake — 17 to 20 digits.</p>}
            </div>
          </div>
        </article>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.25rem' }}>
          <button className="button-primary" type="submit" disabled={!isValid || update.isPending}>
            {update.isPending ? 'Saving…' : 'Save changes'}
          </button>
          {saved && <span style={{ color: 'var(--positive)', fontSize: 13 }}>Saved</span>}
          {update.isError && <span style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t save. Try again.</span>}
        </div>
      </form>

      <BroadcastOptOutCard guildId={selectedGuildId} />
    </>
  );
}
